import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Checkbox } from '@/components/ui/Checkbox';
import { toast } from '@/hooks/useToast';
import { tenantKey } from '@/lib/queryKeys';
import { useCreateArrival, useErMeta } from './hooks';
import { searchPatients } from './api';
import { ARRIVAL_MODE_LABEL, errorInfo } from './ui';
import type { ArrivalInput, ArrivalMode, ArrivalResult, BroughtBy, PatientHit } from './types';
import VoiceFormFiller, { type VoiceField } from '@/components/VoiceFormFiller';
import { numField, selectField, textField, vNum, vOneOf, vText, type Extracted } from '@/lib/voiceFill';

const GENDERS = ['Male', 'Female', 'Other', 'Unknown'] as const;
const BROUGHT_KEYS: Array<{ key: keyof BroughtBy; label: string }> = [
  { key: 'name', label: 'Name of the person who brought the patient' },
  { key: 'relationship', label: 'Their relationship to the patient' },
  { key: 'phone', label: 'Their phone number' },
  { key: 'address', label: 'Their address' },
  { key: 'police_station', label: 'Police station / officer who brought the patient' },
  { key: 'ambulance_service', label: 'Ambulance service, e.g. 108' },
  { key: 'vehicle_no', label: 'Ambulance / vehicle number' },
  { key: 'crew_name', label: 'Ambulance crew name(s)' },
];

/**
 * New Arrival (design §7). The ≤ 30 s rule: the big button needs NOTHING —
 * an unknown patient, ER visit and encounter are created in one transaction
 * and the screen lands on Triage. Everything below it is optional and can be
 * resolved later (D2). "Brought by" is PROMPTED for an MLC / ambulance /
 * police arrival (D13) but never required here — it is never allowed to
 * block treatment.
 */
export default function NewArrivalDialog({ open, onClose, onCreated }: {
  open: boolean;
  onClose: () => void;
  onCreated: (enc: ArrivalResult) => void;
}) {
  const meta = useErMeta();
  const create = useCreateArrival();
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<PatientHit | null>(null);
  const [form, setForm] = useState<ArrivalInput>({});
  const [brought, setBrought] = useState<BroughtBy>({});
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) { setQ(''); setPicked(null); setForm({}); setBrought({}); setError(null); setFields({}); }
  }, [open]);

  const hits = useQuery({
    queryKey: tenantKey('er', 'patient-search', q.trim()),
    queryFn: () => searchPatients(q.trim()),
    enabled: open && !picked && q.trim().length >= 2,
  });

  const set = <K extends keyof ArrivalInput>(k: K, v: ArrivalInput[K]) => setForm((f) => ({ ...f, [k]: v }));
  const setB = (k: keyof BroughtBy, v: string | boolean) => setBrought((b) => ({ ...b, [k]: v }));

  // Dictation fills only the optional "what you know" part — the one-click
  // unknown-patient button above never waits on it. The MLC flag and payer
  // stay taps: they are decisions, not things to transcribe.
  const modes = meta.data?.arrival_modes ?? [];
  const voiceFields: VoiceField[] = [
    ...(picked ? [] : [
      textField('name', 'Patient name, only if told'),
      selectField('gender', 'Sex', GENDERS),
      numField('approx_age_years', 'Approximate age in years'),
      textField('mobile', 'Patient mobile number'),
    ]),
    ...(modes.length ? [selectField('arrival_mode', `Mode of arrival — ${modes.map((m) => `${m} (${ARRIVAL_MODE_LABEL[m] ?? m})`).join(', ')}`, modes)] : []),
    textField('chief_complaint', 'Chief complaint, e.g. RTA, head injury'),
    ...BROUGHT_KEYS.map((b) => textField(`brought_${b.key}`, b.label)),
  ];
  const applyVoice = (x: Extracted) => {
    setForm((f) => {
      const next = { ...f };
      if (!picked) {
        const name = vText(x, 'name'); if (name) next.name = name;
        const g = vOneOf(x, 'gender', GENDERS); if (g) next.gender = g;
        const age = vNum(x, 'approx_age_years'); if (age !== undefined && age >= 0 && age <= 130) next.approx_age_years = Math.round(age);
        const mob = vText(x, 'mobile')?.replace(/\D/g, ''); if (mob && mob.length >= 10) next.mobile = mob.slice(-10);
      }
      const mode = vOneOf(x, 'arrival_mode', modes); if (mode) next.arrival_mode = mode as ArrivalMode;
      const cc = vText(x, 'chief_complaint'); if (cc) next.chief_complaint = cc;
      return next;
    });
    // Brought-by boxes only render for an ambulance/police/Good Samaritan or
    // MLC arrival — never fill fields the user can't see before saving.
    const mode = vOneOf(x, 'arrival_mode', modes) ?? form.arrival_mode;
    const showsBroughtBy = form.is_mlc || mode === 'ambulance' || mode === 'police' || mode === 'good_samaritan';
    if (!showsBroughtBy) return;
    const b: BroughtBy = {};
    for (const k of BROUGHT_KEYS) { const t = vText(x, `brought_${k.key}`); if (t) (b as Record<string, string>)[k.key] = t; }
    if (Object.keys(b).length) setBrought((cur) => ({ ...cur, ...b }));
  };

  const promptBroughtBy = form.is_mlc || form.arrival_mode === 'ambulance' || form.arrival_mode === 'police' || form.arrival_mode === 'good_samaritan';

  const submit = async (quick: boolean) => {
    setError(null); setFields({});
    const body: ArrivalInput = quick ? {} : {
      ...form,
      ...(picked ? { patient_id: picked.id, name: undefined, gender: undefined, approx_age_years: undefined, mobile: undefined } : {}),
      arrival_mode: form.arrival_mode || undefined,
      approx_age_years: form.approx_age_years === '' ? undefined : form.approx_age_years,
      brought_by: Object.keys(brought).length ? brought : undefined,
    };
    try {
      const enc = await create.mutateAsync(body);
      if (enc.warnings.patient_recorded_deceased) {
        toast({ title: 'Patient is recorded as deceased', description: 'Treatment continues — correct the record in Patient Records afterwards.', variant: 'danger' });
      }
      if (enc.warnings.open_op_visit) {
        const v = enc.warnings.open_op_visit;
        toast({
          title: v.policy === 'auto_close' ? 'Open OP visit closed' : 'Patient has an open OP visit',
          description: v.visits.map((x) => x.visit_id).join(', ') + (v.policy === 'auto_close' ? ' was closed automatically.' : ' was left open.'),
          variant: 'warn',
        });
      }
      onCreated(enc);
    } catch (e) {
      const info = errorInfo(e);
      setError(info.message); setFields(info.fields);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="touch-form max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>🚑 New arrival</DialogTitle>
          <DialogDescription>Nothing is required. Register now and identify the patient later.</DialogDescription>
        </DialogHeader>

        <Button variant="danger" size="lg" full loading={create.isPending} onClick={() => submit(true)}>
          Register unknown patient now → Triage
        </Button>

        <div className="relative my-1 text-center text-[11px] uppercase tracking-wide text-ink-muted">
          <span className="bg-card px-2">or add what you know</span>
        </div>

        {error && <div className="rounded-lg bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{error}</div>}

        <VoiceFormFiller fields={voiceFields} onFields={applyVoice} label="🎙 Dictate arrival details"
          hint="Male about 40, brought by 108 ambulance TN 38 AB 1234, road traffic accident, head injury" />

        <FormField label="Known patient? Search UHID / mobile / name">
          {picked ? (
            <div className="flex items-center justify-between rounded-lg border border-border bg-muted px-3 py-2 text-[13px]">
              <span><b>{picked.name}</b> · {picked.uhid}{picked.age != null ? ` · ${picked.age}y` : ''}{picked.gender ? ` · ${picked.gender}` : ''}</span>
              <Button variant="link" size="sm" onClick={() => setPicked(null)}>Change</Button>
            </div>
          ) : (
            <>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type at least 2 characters" />
              {(hits.data?.length ?? 0) > 0 && (
                <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-border">
                  {hits.data!.map((p) => (
                    <button key={p.id} type="button" onClick={() => setPicked(p)}
                      className="flex w-full items-center justify-between border-b border-border px-3 py-1.5 text-left text-[13px] last:border-b-0 hover:bg-muted">
                      <span>{p.name} <span className="text-ink-muted">· {p.uhid}</span></span>
                      <span className="text-[11px] text-ink-muted">{p.mobile ?? ''}{p.deceased ? ' · recorded deceased' : ''}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </FormField>

        {!picked && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <FormField label="Name (if told)" className="col-span-2" error={fields.name}>
              <Input value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} placeholder="Leave blank if unknown" />
            </FormField>
            <FormField label="Sex" error={fields.gender}>
              <NativeSelect value={form.gender ?? ''} onChange={(e) => set('gender', e.target.value || undefined)}>
                <option value="">—</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
                <option value="Unknown">Unknown</option>
              </NativeSelect>
            </FormField>
            <FormField label="Approx. age" error={fields.approx_age_years}>
              <Input type="number" min={0} max={130} value={form.approx_age_years ?? ''}
                onChange={(e) => set('approx_age_years', e.target.value === '' ? '' : Number(e.target.value))} />
            </FormField>
            <FormField label="Mobile (if known)" className="col-span-2" error={fields.mobile}>
              <Input value={form.mobile ?? ''} onChange={(e) => set('mobile', e.target.value)} inputMode="tel" />
            </FormField>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Mode of arrival">
            <NativeSelect value={form.arrival_mode ?? ''} onChange={(e) => set('arrival_mode', e.target.value as ArrivalMode | '')}>
              <option value="">—</option>
              {(meta.data?.arrival_modes ?? []).map((m) => <option key={m} value={m}>{ARRIVAL_MODE_LABEL[m] ?? m}</option>)}
            </NativeSelect>
          </FormField>
          <FormField label="Payer">
            <NativeSelect value={form.payer_category ?? ''} onChange={(e) => set('payer_category', e.target.value || undefined)}>
              <option value="">Self pay (default)</option>
              {(meta.data?.payer_schemes ?? []).filter((s) => s.code !== 'self').map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
            </NativeSelect>
          </FormField>
          <FormField label="Chief complaint" className="col-span-2">
            <Input value={form.chief_complaint ?? ''} onChange={(e) => set('chief_complaint', e.target.value)} placeholder="e.g. RTA, head injury" />
          </FormField>
        </div>

        <label className="flex items-center gap-2 text-[13px] font-medium">
          <Checkbox checked={!!form.is_mlc} onCheckedChange={(c) => set('is_mlc', c === true)} />
          Medico-legal case (MLC) — flag now; the register is completed later
        </label>

        {promptBroughtBy && (
          <div className="rounded-lg border border-warn-soft bg-warn-soft/40 p-3">
            <div className="mb-2 text-[12px] font-semibold text-warn-ink">
              Who brought the patient?{meta.data?.mlc_profile?.require_brought_by_particulars ? ' (required for the accident register under the active MLC profile — can be completed later)' : ''}
            </div>
            <label className="mb-2 flex items-center gap-2 text-[12px]">
              <Checkbox checked={!!brought.declined_good_samaritan} onCheckedChange={(c) => setB('declined_good_samaritan', c === true)} />
              A Good Samaritan brought the patient and declined to give details (cannot be compelled)
            </label>
            {!brought.declined_good_samaritan && (
              <div className="grid grid-cols-2 gap-2">
                {form.arrival_mode === 'ambulance' ? (
                  <>
                    <Input placeholder="Ambulance service (e.g. 108)" value={brought.ambulance_service ?? ''} onChange={(e) => setB('ambulance_service', e.target.value)} />
                    <Input placeholder="Vehicle number" value={brought.vehicle_no ?? ''} onChange={(e) => setB('vehicle_no', e.target.value)} />
                    <Input placeholder="Crew name(s)" className="col-span-2" value={brought.crew_name ?? ''} onChange={(e) => setB('crew_name', e.target.value)} />
                  </>
                ) : (
                  <>
                    <Input placeholder="Name" value={brought.name ?? ''} onChange={(e) => setB('name', e.target.value)} />
                    <Input placeholder="Relationship" value={brought.relationship ?? ''} onChange={(e) => setB('relationship', e.target.value)} />
                    <Input placeholder="Phone" value={brought.phone ?? ''} onChange={(e) => setB('phone', e.target.value)} />
                    <Input placeholder="Address" value={brought.address ?? ''} onChange={(e) => setB('address', e.target.value)} />
                    {form.arrival_mode === 'police' && (
                      <Input placeholder="Police station / officer" className="col-span-2" value={brought.police_station ?? ''} onChange={(e) => setB('police_station', e.target.value)} />
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button loading={create.isPending} onClick={() => submit(false)}>Register & triage</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
